// react
import { useCallback } from 'react';

// types
import { IfrPlan, IfrPlanVersion, Loan, Project } from '../@types';
import { ifrMonthColumnNames } from '../@types/unions';
import { IfrRow } from '../components/pages/Project/IfrPlan/IfrPlan';

// i18n
import { useTranslation } from 'react-i18next';

// styles
import { printIfrStyles } from '../styles/print.ifr.styles';

// utils
import { formatNumber } from '../utils/format';
import { formatDate, formatDateRange } from '../utils/dates';

export interface UsePrintIfrPlanProps {
	ifrPlan?: IfrPlan | null;
	ifrPlanVersion?: IfrPlanVersion | null;
	project?: Project | null;
	loans: Loan[];
	rowsCashIn: IfrRow[];
	rowsCashOutCategory: IfrRow[];
	rowsCashOutComponent: IfrRow[];
	rowsCheck: IfrRow[];
	rowDataMonthHeaders: Record<string, string>;
}

export const useIfrPlanPrint = ({
	ifrPlan,
	ifrPlanVersion,
	project,
	loans,
	rowsCashIn,
	rowsCashOutCategory,
	rowsCashOutComponent,
	rowsCheck,
	rowDataMonthHeaders,
}: UsePrintIfrPlanProps) => {
	// i18n
	const { t } = useTranslation();

	const generatePrintContent = useCallback((): string => {
		if (!loans.length) return '';

		// Number formatting options for consistent display
		const numberFormatOptions = {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		};

		// Generate content for each loan/financing source
		const loanPages = loans
			.map((loan, index) => {
				// Filter rows for this loan and exclude header rows (empty name + string total)
				const cashInRowsForPrint = rowsCashIn
					.filter((row) => (row.loan as Loan).id === loan.id)
					.filter((row) => !(row.name === '' && typeof row.total === 'string'));

				const categoryRowsForPrint = rowsCashOutCategory
					.filter((row) => (row.loan as Loan).id === loan.id)
					.filter((row) => !(row.name === '' && typeof row.total === 'string'));

				const componentRowsForPrint = rowsCashOutComponent
					.filter((row) => (row.loan as Loan).id === loan.id)
					.filter((row) => !(row.name === '' && typeof row.total === 'string'));

				const checkRowsForPrint = rowsCheck
					.filter((row) => (row.loan as Loan).id === loan.id)
					.filter((row) => !(row.name === '' && typeof row.total === 'string'));

				const generateTableHTML = (title: string, rows: IfrRow[]): string => {
					if (!rows.length) return '';

					return `
					<div class="table-section">
						<h3>${title}</h3>
						<table>
							<thead>
								<tr>
									<th class="col-label">Name</th>
									${ifrMonthColumnNames
										.map(
											(month) => `<th class="col-data">${rowDataMonthHeaders[month] || month}</th>`
										)
										.join('')}
									<th class="col-summary">Total</th>
								</tr>
							</thead>
							<tbody>
								${rows
									.map((row) => {
										const isTotalRow =
											row.name === t('total') ||
											row.name === t('ifrPlan.categoryComponentDifference');
										const rowClass = isTotalRow ? 'row-summary' : 'data-row';

										return `
										<tr class="${rowClass}">
											<td class="col-label">${row.name || ''}</td>
											${ifrMonthColumnNames
												.map((month) => {
													const value = row[month];
													const displayValue =
														typeof value === 'number'
															? formatNumber(value, numberFormatOptions)
															: formatNumber(0, numberFormatOptions);
													return `<td class="col-data">${displayValue}</td>`;
												})
												.join('')}
											<td class="col-summary">${
												typeof row.total === 'number'
													? formatNumber(row.total, numberFormatOptions)
													: row.total || ''
											}</td>
										</tr>
									`;
									})
									.join('')}
							</tbody>
						</table>
					</div>
				`;
				};

				const period = formatDateRange({
					dateStart: ifrPlan?.dateFiscalYearStart,
					dateEnd: ifrPlan?.dateFiscalYearEnd,
					format: 'YYYY-MM-DD',
					t,
				});

				return `
				<div class="page">
					<div class="print-header">
						<h1>${ifrPlan?.name || 'IFR Plan'}</h1>
						<div class="print-subtitle">
							<span><strong>Project:</strong> ${project?.name || ''}</span>
							<span><strong>Financing Source:</strong> ${loan.code}</span>
							<span><strong>Currency:</strong> ${ifrPlan?.currency || ''}</span>
							${period && `<span><strong>Period:</strong> ${period}</span>`}
							<span><strong>Version:</strong> ${ifrPlanVersion?.id || ''} (${ifrPlanVersion?.status || ''})</span>
						</div>
					</div>
					
					${generateTableHTML(t('tableSheets.cashIn.title'), cashInRowsForPrint)}
					${generateTableHTML(t('tableSheets.cashOutCategories.title'), categoryRowsForPrint)}
					${generateTableHTML(t('tableSheets.cashOutComponents.title'), componentRowsForPrint)}
					${generateTableHTML(t('tableSheets.checks.title'), checkRowsForPrint)}
				</div>
			`;
			})
			.join('');

		return loanPages;
	}, [
		loans,
		rowsCashIn,
		rowsCashOutCategory,
		rowsCashOutComponent,
		rowsCheck,
		ifrPlan,
		project,
		ifrPlanVersion,
		rowDataMonthHeaders,
		t,
	]);

	const handlePrint = useCallback(() => {
		// Open a new window for printing
		const printWindow = window.open('', '_blank');
		if (!printWindow) return;

		// Generate the print content
		const printContent = generatePrintContent();
		const dateToday = formatDate({
			date: new Date(),
			format: 'YYYY-MM-DD',
		});
		const documentTitle = `${project?.idWbg}-plan-${dateToday}`;
		const doc = printWindow.document;

		// Create and set the HTML structure
		doc.head.innerHTML = `
			<title>${documentTitle}</title>
			<style>
				${printIfrStyles}
			</style>
		`;

		doc.body.innerHTML = printContent;

		// Use requestAnimationFrame to ensure DOM is rendered before printing
		printWindow.requestAnimationFrame(() => {
			printWindow.focus();
			printWindow.print();
			printWindow.close();
		});
	}, [generatePrintContent, project]);

	return {
		handlePrint,
	};
};
